import { useEffect, useMemo, useState } from 'react';

import { translation, format, TranslatedComponent } from '@hh.ru/front-static-app';
import { BottomSheet, BottomSheetFooter, Button, Title } from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { SPALink } from '@hh.ru/redux-spa-middleware';

import { getProfessionRouteHref } from 'src/app/routesUtils';
import AccordionCareerTrack from 'src/widgets/CareerTrack/AccordionCareerTrack';
import CardProfession from 'src/widgets/CareerTrack/CardProfession';
import CarouselCourseselPrograms from 'src/widgets/CareerTrack/CarouselCourses';

import styles from './styles.less';

interface BottomsheetProfessionProps {
    selectedProfessionIndex: number | null;
    onClose: () => void;
}

const TrlKeys = {
    carouselCoursesTitle: 'career-platform.widgets.careerTrack.carouselCourses.title',
    toCourses: 'career-platform.widgets.careerTrack.professionCard.toCourses',
    toProfession: 'career-platform.widgets.careerTrack.professionCard.toProfession',
    close: 'career-platform.widgets.careerTrack.professionCard.close',
};

const BottomsheetProfession: TranslatedComponent<BottomsheetProfessionProps> = ({
    selectedProfessionIndex,
    onClose,
    trls,
}) => {
    const careerTrack = useSelectorNonNullable((state) => state.widgetCareerTrack);
    const targetProfession = useMemo(
        () => (selectedProfessionIndex !== null ? careerTrack?.targetProfessionList[selectedProfessionIndex] : null),
        [selectedProfessionIndex, careerTrack]
    );

    const [visibleBottomsheet, setVisibleBottomsheet] = useState(!!targetProfession);

    const handleCloseBottomsheet = () => {
        setVisibleBottomsheet(false);
        setTimeout(onClose, 400);
    };

    useEffect(() => {
        setVisibleBottomsheet(!!targetProfession);
    }, [targetProfession]);

    if (!targetProfession) {
        return null;
    }

    return (
        <BottomSheet
            onClose={handleCloseBottomsheet}
            visible={visibleBottomsheet}
            footer={
                <BottomSheetFooter>
                    <Button
                        Element={SPALink}
                        to={getProfessionRouteHref(targetProfession.profession.id, { section: 'courses' })}
                        isSeoLink
                        size="medium"
                        mode="primary"
                        stretched
                    >
                        {trls[TrlKeys.toCourses]}
                    </Button>
                    <Button
                        Element={SPALink}
                        to={getProfessionRouteHref(targetProfession.profession.id)}
                        isSeoLink
                        size="medium"
                        mode="secondary"
                        stretched
                    >
                        {trls[TrlKeys.toProfession]}
                    </Button>
                    <Button size="medium" mode="tertiary" stretched onClick={handleCloseBottomsheet}>
                        {trls[TrlKeys.close]}
                    </Button>
                </BottomSheetFooter>
            }
        >
            <div className={styles.bottomsheetContent}>
                <CardProfession selectedProfessionIndex={selectedProfessionIndex} hideButtons />
                <AccordionCareerTrack />
                {!!targetProfession.courses.length && (
                    <>
                        <div className={styles.title}>
                            <Title Element="h4" size="medium">
                                {' '}
                                {format(trls[TrlKeys.carouselCoursesTitle], {
                                    '{0}': targetProfession.profession.name,
                                })}
                            </Title>
                        </div>
                        <CarouselCourseselPrograms edProgram={targetProfession.courses} />
                    </>
                )}
            </div>
        </BottomSheet>
    );
};

export default translation(BottomsheetProfession);
